import { type JSX, type ReactNode, useLayoutEffect, useRef } from 'react';

import { zeile as kundenzeile, type Kundenzeile } from '../api/kunden';
import { ApiFehler } from '../api/fehler';
import { Suchfenster } from './Suchfenster';

/**
 * Das Kundenfeld der Anlegen-Masken (M-07; Auftrag CRM-172).
 *
 * "Es gibt keine zweite Art, einen Datensatz zu waehlen: keine Auswahlliste,
 * kein eigener Dialog je Feld." Neues Angebot, neuer Auftrag, neue Rechnung
 * und neuer Rahmenauftrag waehlen den Kunden darum alle ueber das
 * Suchfenster — vorher hatte jede ausser dem Angebot eine eigene
 * Trefferliste mit eigenem Takt und eigener Zeichengrenze.
 *
 * ZWEI TEILE UND NICHT EINER: Das Feld steht in der Erfassungskarte, das
 * Fenster ausserhalb. Die Karte ist ein <form>; ein Suchfeld darin schickte
 * mit Enter die ganze Erfassung ab statt zu suchen.
 */
export function Kundenfeld({
  kennung,
  kunde,
  offen,
  beiSuchen,
  children,
}: {
  /** Kennung des Feldes; die Beschriftung traegt `${kennung}-bez`. */
  readonly kennung: string;
  readonly kunde: Kundenzeile | undefined;
  /** Ob das Suchfenster gerade offen ist. */
  readonly offen: boolean;
  readonly beiSuchen: () => void;
  /** Hilfetext unter dem Feld. */
  readonly children?: ReactNode;
}): JSX.Element {
  /*
   * DER FOKUS KOMMT ZURUECK UND OEFFNET NICHT NOCH EINMAL. Das Fenster gibt
   * beim Schliessen den Fokus an die Schaltflaeche zurueck, von der es kam.
   * Oeffnete der Fokus dort ungeprueft, ginge es nach Esc sofort wieder auf,
   * und man kaeme aus der Maske nicht heraus. Die Rueckgabe geschieht im
   * Aufraeumen des Fensters, also nach dem Layout-Effekt hier und vor dem
   * naechsten Takt.
   */
  const zuvorOffen = useRef(offen);
  const zurueck = useRef(false);
  useLayoutEffect(() => {
    if (zuvorOffen.current && !offen) {
      zurueck.current = true;
      const uhr = setTimeout(() => {
        zurueck.current = false;
      }, 0);
      zuvorOffen.current = offen;
      return () => clearTimeout(uhr);
    }
    zuvorOffen.current = offen;
    return undefined;
  }, [offen]);

  return (
    <div className="feld">
      {/*
        KEIN <label for> AN DER SCHALTFLAECHE. Eine Schaltflaeche ist ein
        beschriftbares Element — das Label ueberschrieb ihren Text, und
        sie hiess fuer eine Vorlesehilfe "Kunde" statt zu sagen, was sie
        tut. Die Beschriftung steht deshalb als Text daneben und wird
        ueber aria-labelledby VORANGESTELLT: "Kunde, Kunde suchen …".
      */}
      <span className="feld__beschriftung" id={`${kennung}-bez`}>
        Kunde
      </span>
      {kunde === undefined ? (
        <button
          id={kennung}
          type="button"
          className="feld__eingabe feld__waehlen"
          aria-haspopup="dialog"
          aria-labelledby={`${kennung}-bez ${kennung}`}
          onClick={beiSuchen}
          onFocus={() => {
            if (zurueck.current) {
              zurueck.current = false;
              return;
            }
            beiSuchen();
          }}
        >
          Kunde suchen …
        </button>
      ) : (
        <p className="kanalliste__wert">
          {kunde.kundennummer} · {kunde.name}{' '}
          <button type="button" className="knopf knopf--klein" onClick={beiSuchen}>
            Anderen wählen
          </button>
        </p>
      )}
      {children}
    </div>
  );
}

/**
 * Das Suchfenster im Auswahlbetrieb, fest auf die Art Kunde (M-07).
 *
 * DIE GANZE KUNDENZEILE UND NICHT NUR DIE WAHL. Das Fenster gibt Kennung und
 * Bezeichnung zurueck. Die Masken brauchen mehr: die Kundennummer fuer die
 * Anzeige und die Kundenart fuer die Preisfuehrung der neuen Rechnung
 * (RND-08). Eine angenommene Kundenart waere eine zweite Quelle, die einen
 * Privatkunden still netto fuehrte — darum holt die Wahl die Zeile aus der
 * Akte, wie der aus der Akte mitgebrachte Kunde auch.
 */
export function Kundensuchfenster({
  beiWahl,
  beiFehler,
  beiSchliessen,
}: {
  readonly beiWahl: (kunde: Kundenzeile) => void;
  readonly beiFehler: (fehler: ApiFehler) => void;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  return (
    <Suchfenster
      betriebsart="auswaehlen"
      arten={['kunde']}
      festeArt="kunde"
      beiSchliessen={beiSchliessen}
      beiWahl={(wahl) => {
        beiSchliessen();
        void kundenzeile(wahl.id)
          .then(beiWahl)
          .catch((ursache: unknown) => beiFehler(ursache as ApiFehler));
      }}
    />
  );
}
